
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { SymptomSession, Language, VisualDensity } from '../types';
import { t } from '../locales';
import SessionEditModal from './SessionEditModal';
import ManualEntryModal from './ManualEntryModal';
import AddMethodModal from './AddMethodModal';
import { ParsedItem } from '../services/textParsingService';

interface SymptomListProps {
  session: SymptomSession;
  onAddMore: (sessionId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onUpdateSessionTime: (sessionId: string, newDate: Date) => void;
  onUpdateItem: (itemId: string, newSeverity: number) => void;
  onManualAdd: (items: ParsedItem[]) => Promise<void>;
  isRecordingThisSession: boolean;
  language: Language;
  isFirstSession?: boolean; // Prop to determine if this is the first item for tour tagging
  density?: VisualDensity; // New density prop
  onMerge?: (sourceId: string) => void; // New prop for merging
}

const SymptomList: React.FC<SymptomListProps> = ({ 
    session, 
    onAddMore, 
    onDeleteItem, 
    onDeleteSession, 
    onUpdateSessionTime,
    onUpdateItem,
    onManualAdd,
    isRecordingThisSession, 
    language,
    isFirstSession = false,
    density = 'standard',
    onMerge
}) => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [isEditTimeOpen, setIsEditTimeOpen] = useState(false);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Density-derived styles
  const containerRounded = density === 'compact' ? 'rounded-md' : density === 'comfort' ? 'rounded-xl' : 'rounded-lg';
  const headerPadding = density === 'compact' ? 'px-3 py-2' : density === 'comfort' ? 'px-6 py-4' : 'px-4 py-3';
  const headerIconSize = density === 'compact' ? 'p-1.5' : 'p-2';
  const headerTextSize = density === 'compact' ? 'text-xs' : 'text-sm font-medium';
  const rowPadding = density === 'compact' ? 'py-1' : density === 'comfort' ? 'py-4' : 'py-2';
  const itemTextSize = density === 'compact' ? 'text-xs' : 'text-sm';
  const cellPaddingLeft = density === 'compact' ? 'pl-2' : 'pl-3';

  const getContextLabel = (date: Date) => {
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return `${time}`;
  };

  const handleDeleteSession = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onDeleteSession(session.id);
  };

  const handleDeleteItem = (e: React.MouseEvent, itemId: string) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      onDeleteItem(itemId);
  };

  const handleAddMethodSelect = (method: 'audio' | 'text' | 'barcode' | 'camera') => {
      setIsAddMethodOpen(false); // Close modal manually
      if (method === 'audio') {
          onAddMore(session.id);
      } else if (method === 'text') {
          setIsManualEntryOpen(true);
      }
  };

  const getSeverityColor = (score: number) => {
    if (score <= 2) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    if (score <= 4) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    if (score <= 6) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    if (score <= 8) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  };

  const handleDragStart = (e: React.DragEvent) => {
      e.dataTransfer.setData('text/plain', session.id);
      e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!isDragOver) setIsDragOver(true);
  };

  const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const sourceId = e.dataTransfer.getData('text/plain');
      if (sourceId && sourceId !== session.id && onMerge) {
          onMerge(sourceId);
      }
  };

  return (
    <>
    <div 
        draggable={!isRecordingThisSession}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={`bg-white dark:bg-gray-800 shadow-sm overflow-hidden border transition-all mb-4 ${containerRounded} ${isRecordingThisSession ? 'border-purple-400 ring-1 ring-purple-100' : 'border-gray-100 dark:border-gray-700'} ${isDragOver ? 'border-dashed border-orange-500 ring-2 ring-orange-500/20 scale-[1.02] bg-orange-50/50' : ''}`}
    >
      {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
              <div className="bg-orange-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg animate-pulse uppercase tracking-widest">
                  {language === 'fr' ? 'Relâcher pour fusionner' : 'Drop to Merge'}
              </div>
          </div>
      )}

      <div 
        onClick={() => setIsListOpen(!isListOpen)}
        className={`${headerPadding} flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors select-none group`}
      >
        <div className="flex items-center gap-3">
           <div className={`${headerIconSize} rounded-full ${isRecordingThisSession ? 'bg-purple-100 text-purple-600' : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'}`}>
             {isRecordingThisSession ? (
                <div className="flex items-center gap-1 px-1">
                  <div className="w-1 h-3 bg-purple-500 animate-pulse"></div>
                  <div className="w-1 h-4 bg-purple-500 animate-pulse delay-75"></div>
                  <div className="w-1 h-2 bg-purple-500 animate-pulse delay-150"></div>
                </div>
             ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
             )}
           </div>
           <div>
             <h3 className={`${headerTextSize} text-gray-800 dark:text-gray-200`}>{getContextLabel(session.timestamp)}</h3>
             <p className="text-[10px] text-gray-500 dark:text-gray-400">{session.items.length} {t(language, 'symptomsLoggedCount')}</p>
           </div>
        </div>
        
        <div className="flex items-center gap-2">
            <button 
                id={isFirstSession ? "tour-symptom-session-time" : undefined}
                onClick={(e) => { e.stopPropagation(); setIsEditTimeOpen(true); }} 
                className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 dark:text-gray-500 transition-colors"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
            </button>

            <button 
                onClick={(e) => { e.stopPropagation(); setIsAddMethodOpen(true); }}
                disabled={isRecordingThisSession}
                className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors ${isRecordingThisSession ? 'opacity-0' : 'opacity-100'}`}
                title={t(language, 'addSymptomTooltip')}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
            </button>

            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isListOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            <button onClick={handleDeleteSession} className="p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-300 hover:text-red-500 transition-colors ml-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
        </div>
      </div>

      {isListOpen && (
        <div className="border-t border-gray-100 dark:border-gray-700 overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {session.items.length === 0 ? (
                <tr><td colSpan={3} className="px-6 py-6 text-center text-xs text-gray-400 italic">{t(language, 'listeningSymptoms')}</td></tr>
              ) : (
                session.items.map((item, index) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className={`${cellPaddingLeft} ${rowPadding} ${itemTextSize} text-gray-500 dark:text-gray-400 w-px whitespace-nowrap pr-2`}>{item.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                      <td className={`px-2 ${rowPadding} w-full overflow-hidden`}>
                          <div className="flex items-center justify-between gap-2 w-full">
                            <span className={`${itemTextSize} font-medium text-gray-900 dark:text-gray-200 capitalize truncate flex-1`}>{item.symptom}</span>
                            <button 
                                id={isFirstSession && index === 0 ? "tour-symptom-item-severity" : undefined}
                                onClick={(e) => { e.stopPropagation(); setEditingItemId(item.id); }} 
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border transition-all hover:scale-105 shrink-0 ${getSeverityColor(item.severity)} border-transparent hover:border-current cursor-pointer`}
                            >
                                {item.severity} / 10
                            </button>
                          </div>
                      </td>
                      <td className={`pr-4 pl-2 ${rowPadding} text-right w-10 whitespace-nowrap`}>
                          <button onClick={(e) => handleDeleteItem(e, item.id)} className="text-gray-400 hover:text-red-500 p-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                      </td>
                    </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {isEditTimeOpen && <SessionEditModal isOpen={isEditTimeOpen} onClose={() => setIsEditTimeOpen(false)} currentDate={session.timestamp} onSave={(newDate) => onUpdateSessionTime(session.id, newDate)} language={language} />}
    {isManualEntryOpen && <ManualEntryModal isOpen={isManualEntryOpen} onClose={() => setIsManualEntryOpen(false)} type="symptom" onSave={(items) => onManualAdd(items)} language={language} />}
    {isAddMethodOpen && <AddMethodModal isOpen={isAddMethodOpen} onClose={() => setIsAddMethodOpen(false)} onSelect={handleAddMethodSelect} language={language} type="symptom" />}
    {editingItemId && <SeveritySelectorModal isOpen={!!editingItemId} onClose={() => setEditingItemId(null)} currentSeverity={session.items.find(i => i.id === editingItemId)?.severity || 5} onSelect={(newVal) => { if (editingItemId) onUpdateItem(editingItemId, newVal); setEditingItemId(null); }} language={language} getColor={getSeverityColor} />}
    </>
  );
};

// ... SeveritySelectorModal ...
const SeveritySelectorModal: React.FC<{ isOpen: boolean; onClose: () => void; currentSeverity: number; onSelect: (val: number) => void; language: Language; getColor: (val: number) => string; }> = ({ isOpen, onClose, currentSeverity, onSelect, language, getColor }) => {
    if (!isOpen) return null;
    const scaleDescriptions: Record<number, string> = { 0: t(language, 'scale0'), 1: t(language, 'scale1_2'), 2: t(language, 'scale1_2'), 3: t(language, 'scale3_4'), 4: t(language, 'scale3_4'), 5: t(language, 'scale5_6'), 6: t(language, 'scale5_6'), 7: t(language, 'scale7_8'), 8: t(language, 'scale7_8'), 9: t(language, 'scale9'), 10: t(language, 'scale10') };
    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
            <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden relative z-10 animate-popIn flex flex-col max-h-[85vh]">
                <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center sticky top-0">
                    <h3 className="font-bold text-gray-800 dark:text-white">{t(language, 'changeSeverity')}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="overflow-y-auto p-2">
                    {[...Array(11)].map((_, i) => (
                        <button key={i} onClick={() => onSelect(i)} className={`w-full text-left p-3 rounded-xl flex items-center gap-4 transition-all mb-1 ${currentSeverity === i ? 'bg-gray-100 dark:bg-gray-700 ring-2 ring-emerald-500 ring-inset' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                            <span className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm ${getColor(i)}`}>{i}</span>
                            <div className="flex flex-col"><span className="font-bold text-gray-800 dark:text-gray-200 text-sm">Level {i}</span><span className="text-xs text-gray-500 dark:text-gray-400">{scaleDescriptions[i]}</span></div>
                        </button>
                    ))}
                </div>
            </div>
        </div>, document.body);
};

export default SymptomList;
